import api from '@/lib/axios';

export interface CostReportParams {
  companyId: string;
  startDate?: string;
  endDate?: string;
  productId?: string;
  groupBy?: 'product' | 'day' | 'month';
}

export interface CostReportItem {
  label: string;
  sku?: string;
  category?: string;
  productId?: string;
  revenue: number;
  cost: number;
  profit: number;
  margin: number;
  quantity: number;
  items: number;
}

export interface CostReportSummary {
  totalRevenue: number;
  totalCost: number;
  totalProfit: number;
  avgMargin: number;
  totalItems: number;
  totalQuantity: number;
  totalSales: number;
  startDate: string;
  endDate: string;
}

export interface DashboardKpi {
  value: number;
  change: number;
  trend: 'up' | 'down';
  detail?: string;
}

export interface DashboardData {
  kpis: {
    todaySales: DashboardKpi;
    todayProfit: DashboardKpi;
    productsSold: DashboardKpi;
    totalCustomers: DashboardKpi;
    criticalStock: DashboardKpi;
    pendingOrders: DashboardKpi;
    todayReturns: DashboardKpi;
  };
  recentSales: Array<{
    id: string;
    folio: string;
    customer: string;
    store?: string | null;
    total: number;
    items: number;
    time: string;
    method: string;
  }>;
  criticalStock: Array<{
    id: string;
    name: string;
    stock: number;
    minStock: number;
    category: string;
  }>;
  totalProducts: number;
  monthSales: number;
  todaySalesCount: number;
  salesTrend: Array<{
    date: string;
    total: number;
    count: number;
  }>;
  topProducts: Array<{
    name: string;
    sku: string;
    quantity: number;
    revenue: number;
  }>;
  paymentBreakdown: Record<string, number>;
  recentCreditNotes: Array<{
    id: string;
    folio: string;
    originalSaleFolio: string;
    total: number;
    remaining: number;
    status: string;
    reason: string;
    createdBy: string;
    createdAt: string;
  }>;
  todayReturns: {
    issued: number;
    issuedTotal: number;
    used: number;
    activeBalance: number;
  };
}

export interface StoreOverviewBranch {
  id: string;
  name: string;
  todaySales: number;
  todaySalesCount: number;
  todayProfit: number;
  monthSales: number;
  monthSalesCount: number;
  lastSaleAt: string | null;
}

export interface StoreOverviewData {
  asOf: string;
  totals: {
    todaySales: number;
    todaySalesCount: number;
    todayProfit: number;
    monthSales: number;
    monthSalesCount: number;
  };
  branches: StoreOverviewBranch[];
}

export const reportsService = {
  async getDashboard(companyId: string) {
    const response = await api.get('/reports/dashboard', { params: { companyId } });
    return response.data as DashboardData;
  },

  async getOwnerOverview(companyId: string) {
    const response = await api.get('/reports/owner-overview', { params: { companyId } });
    return response.data as StoreOverviewData;
  },

  async getCosts(params: CostReportParams) {
    const response = await api.get('/reports/costs', { params });
    return response.data as {
      data: CostReportItem[];
      summary: CostReportSummary;
    };
  },
};
